import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { createMergeTagDropdown } from './suggestion_dropdown';

// Matches a complete "{{word}}" placeholder so it can be visually rendered as a chip.
const MERGE_TAG_PATTERN = /\{\{([a-zA-Z0-9_]+)\}\}/g;

// Flattens the dictionary groups (`[{ title, menu: [{ value, title }] }]`) into a
// single searchable list, keeping each item's group title for the dropdown header.
function flattenGroups(groups) {
  return groups.flatMap((group) => {
    const entries = group.menu || (group.value ? [{ value: group.value, title: group.title }] : []);
    return entries.map((entry) => ({ ...entry, group: group.title }));
  });
}

// Tiptap extension powering the contract merge-tag experience.
//
// Design: a merge tag is stored as PLAIN TEXT "{{value}}" in the document, exactly
// like the legacy TinyMCE output, so `Dictionaries::EnrollmentContract#translate`
// keeps working untouched and the generated PDF carries no editor-specific markup.
// The chip look is purely a view-layer decoration — it never reaches getHTML().
export const MergeTags = Extension.create({
  name: 'mergeTags',

  addOptions() {
    return { groups: [] };
  },

  addProseMirrorPlugins() {
    const flatItems = flattenGroups(this.options.groups);

    return [
      // 1. Autocomplete triggered by typing "{{": inserts the literal "{{value}}" token.
      Suggestion({
        editor: this.editor,
        char: '{{',
        startOfLine: false,
        // By default the suggestion only triggers when the char before "{{" is a
        // space or line start; null lets it fire anywhere (e.g. right after a word).
        allowedPrefixes: null,
        items: ({ query }) => {
          const term = query.toLowerCase();
          return flatItems.filter(
            (item) =>
              item.title.toLowerCase().includes(term) || item.value.toLowerCase().includes(term)
          );
        },
        command: ({ editor, range, props }) => {
          editor.chain().focus().insertContentAt(range, `{{${props.value}}} `).run();
        },
        render: createMergeTagDropdown
      }),

      // 2. View-only decorations: paint every "{{word}}" as a chip without mutating
      //    the underlying text. Reopening a saved contract restyles the tokens for free.
      new Plugin({
        key: new PluginKey('mergeTagDecorations'),
        props: {
          decorations(state) {
            const decorations = [];
            state.doc.descendants((node, position) => {
              if (!node.isText) return;
              MERGE_TAG_PATTERN.lastIndex = 0;
              let match = MERGE_TAG_PATTERN.exec(node.text);
              while (match !== null) {
                const from = position + match.index;
                const to = from + match[0].length;
                decorations.push(Decoration.inline(from, to, { class: 'merge-tag' }));
                match = MERGE_TAG_PATTERN.exec(node.text);
              }
            });
            return DecorationSet.create(state.doc, decorations);
          }
        }
      })
    ];
  }
});

export { flattenGroups };
